import { TimeReportResolution } from "model/TimeReport";
import type {
  ApproveTimeReportData,
  TimeReportsV2Item,
} from "services/api/company/timeReports.service";
import {
  formatSignedMinutesDuration,
  minutesBetweenIso,
} from "web-apps/company/utils/timeAndDate";
import {
  getReportedMinutes,
} from "./personView.timeReportsCalculations";
import { resolveTimeReportActionPolicy } from "./timeReportActionPolicy";

export type BulkAction = "retract" | "absence" | "reset" | "reject" | "approve";

/** Canonical display order for bulk actions in the person selection banner. */
export const ACTION_ORDER: BulkAction[] = [
  "retract",
  "absence",
  "reset",
  "reject",
  "approve",
];

const NEGATIVE_TOTAL_RESOLUTIONS = new Set<TimeReportResolution>([
  TimeReportResolution.SICKLEAVE,
  TimeReportResolution.SICKLEAVECHILD,
  TimeReportResolution.VACATION,
  TimeReportResolution.PARENTALLEAVE,
  TimeReportResolution.LEAVEOFABSENCE,
  TimeReportResolution.FUNERALLEAVE,
  TimeReportResolution.STUDYLEAVE,
  TimeReportResolution.LEAVEUNIONASSIGNEMENTS,
  TimeReportResolution.MILITARYSERVICE,
  TimeReportResolution.OTHER,
  TimeReportResolution.WITHDRAWN,
]);

/**
 * Returns signed minutes for display based on time report resolution.
 * Absence and withdrawn resolutions are shown as negative values.
 */
export const resolveSignedMinutesForResolution = (
  totalMinutes: number,
  resolution: TimeReportResolution | null | undefined,
): number => {
  const isNegativeResolution =
    resolution != null &&
    NEGATIVE_TOTAL_RESOLUTIONS.has(resolution) &&
    totalMinutes > 0;
  return isNegativeResolution ? -totalMinutes : totalMinutes;
};

/**
 * Resolves the signed total minutes for a time report item.
 * Absence and withdrawn resolutions should always produce negative totals.
 */
export const resolveSignedTotalMinutes = (item: TimeReportsV2Item): number => {
  const reportedMinutes = getReportedMinutes(item) ?? 0;
  const totalMinutes = reportedMinutes;
  return resolveSignedMinutesForResolution(totalMinutes, item.resolution);
};

/** Formats signed minutes into user-facing duration text (e.g. +1h 30m, -45m). */
export const formatSignedTotalMinutes = (minutes: number): string => {
  return formatSignedMinutesDuration(minutes);
};

const requireTime = (
  value: string | null,
  fieldName:
    | "reportedStartTime"
    | "reportedEndTime"
    | "breakStartTime"
    | "breakEndTime",
  timeReportId: number,
): string => {
  if (!value) {
    throw new Error(
      `Cannot approve time report ${String(timeReportId)}: missing ${fieldName}`,
    );
  }

  return value;
};

/**
 * Builds approve payload for the time-report approve endpoint.
 * Requires report/break range to be present.
 */
export const buildApprovePayload = (
  item: TimeReportsV2Item,
): ApproveTimeReportData => {
  const startTime = requireTime(
    item.reportedStartTime,
    "reportedStartTime",
    item.id,
  );
  const endTime = requireTime(item.reportedEndTime, "reportedEndTime", item.id);
  const breakStartTime = requireTime(
    item.breakStartTime,
    "breakStartTime",
    item.id,
  );
  const breakEndTime = requireTime(item.breakEndTime, "breakEndTime", item.id);

  return {
    comment: "",
    isEditedByCompany: true,
    startTime,
    endTime,
    breakTime: minutesBetweenIso(breakStartTime, breakEndTime),
    breakStartTime,
    breakEndTime,
  };
};

/**
 * Resolves which bulk actions are valid for a single time-report item.
 * The caller can intersect this set across selected items to find common actions.
 */
export const resolveAvailableActions = (
  item: TimeReportsV2Item,
): Set<BulkAction> => {
  const actions = new Set<BulkAction>();
  const policy = resolveTimeReportActionPolicy(item.status, item.resolution);
  if (policy.showRetractAction) actions.add("retract");
  if (policy.showAbsenceAction) actions.add("absence");
  if (policy.showResetAction) actions.add("reset");
  if (policy.showRejectAction) actions.add("reject");
  if (policy.showApproveAction) actions.add("approve");

  return actions;
};
